import { useLanguage } from '../hooks/useLanguage';
import { useEffect, useRef } from 'react';

export default function DeliveryPartners() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const partners = t('delivery.partners');
  const features = t('delivery.features');

  return (
    <section id="delivery" ref={sectionRef} className="section-padding relative bg-navy-700">
      <div className="w-full">
        {/* Header */}
        <div className="text-center mb-20 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-brand-300 text-sm font-semibold tracking-wider mb-6">
            {t('delivery.badge')}
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white">
            {t('delivery.title')}{' '}
            <span className="text-brand-400">{t('delivery.titleHighlight')}</span>
          </h2>
          <p className="mt-6 text-xl text-navy-200 max-w-3xl mx-auto">
            {t('delivery.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Partners Grid */}
          <div className="reveal">
            <div className="grid grid-cols-2 gap-4">
              {Array.isArray(partners) && partners.map((partner, index) => (
                <div
                  key={index}
                  className="group bg-white/5 border border-white/10 rounded-2xl p-6 text-center hover:bg-white/10 transition-all duration-300"
                >
                  <div className="w-14 h-14 mx-auto rounded-xl bg-brand-500 flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <span className="text-white font-bold text-lg">{partner.name.charAt(0)}</span>
                  </div>
                  <h4 className="text-white font-semibold mb-1">{partner.name}</h4>
                  <p className="text-navy-300 text-sm">{partner.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Coverage card */}
            <div className="mt-4 bg-brand-500/10 border border-brand-500/30 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-3">
                <svg className="w-6 h-6 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-white font-semibold">{t('delivery.coverage')}</span>
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="reveal" style={{ transitionDelay: '200ms' }}>
            <div className="space-y-5">
              {Array.isArray(features) && features.map((feature, index) => (
                <div key={index} className="flex items-center gap-4 group">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center">
                    <svg className="w-5 h-5 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-navy-100 text-lg">{feature}</span>
                </div>
              ))}
            </div>

            {/* Map illustration */}
            <div className="mt-12">
              <div className="bg-navy-800/50 border border-white/5 rounded-2xl p-8 text-center">
                <div className="text-5xl mb-3">🇹🇳</div>
                <div className="text-3xl font-bold text-white">24</div>
                <div className="text-navy-300 mb-4">{t('delivery.governoratesCovered')}</div>
                <div className="flex flex-wrap justify-center gap-2">
                  {['Tunis', 'Sfax', 'Sousse', 'Nabeul', 'Bizerte', 'Ariana'].map((city) => (
                    <span key={city} className="px-3 py-1 text-xs bg-white/5 border border-white/10 rounded-full text-navy-200">
                      {city}
                    </span>
                  ))}
                  <span className="px-3 py-1 text-xs bg-brand-500/20 border border-brand-500/30 rounded-full text-brand-300">
                    {t('delivery.moreCities')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
